import {
    getOrderEmailRecipients,
    orderEmailDefaultsFor,
} from '@api/organization';
import { sendProductionOrder, type ProductionOrderListRow } from '@api/work-order';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, Modal, Stack, TagsInput, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FC } from 'react';
import { formatCount } from '@utils/currency';

interface SendProductionOrderModalProps {
    order: ProductionOrderListRow | null;
    onClose: () => void;
    onSent: () => void;
}

/**
 * Sending an order, as an act somebody performs rather than a setting they tick.
 *
 * THIS EXISTS BECAUSE THE FIRST VERSION WAS A CHECKBOX on the creation form. It worked — the
 * PDF rendered, stored and emailed — and it was indistinguishable from nothing happening: no
 * moment of decision, no confirmation, and no way to know which address it had reached until
 * somebody read the database.
 *
 * NAMING THE RECIPIENT IS THE POINT. The address comes off the supplier record, which the
 * person issuing the order did not necessarily fill in and cannot see from here. Showing it
 * before the send is the difference between issuing an order and hoping.
 *
 * A RESEND IS THE SAME ACT. The document is regenerated and overwrites the same object, so
 * there is one order and one PDF however many times it is sent — which is why this asks the
 * same question the second time rather than offering a different, more anxious one.
 */
const SendProductionOrderModal: FC<SendProductionOrderModalProps> = ({
    order,
    onClose,
    onSent,
}) => {
    const { showToastSuccess, showToastError } = useToaster();

    /*
     * The brand's saved recipient book, so this opens with the right people already on it.
     * Fetched once for the screen rather than carried on every order row: it is one small
     * object per brand, and threading it through the order list would have meant reaching
     * OrganizationService from WorkOrderService — the require cycle this codebase gates in CI.
     */
    const config = useQuery({
        queryKey: ['order-email-recipients', 'production-order'],
        queryFn: () => getOrderEmailRecipients('production-order'),
    });

    const [isSending, setIsSending] = useState(false);
    const [isIssuing, setIsIssuing] = useState(false);
    const [confirmingIssue, setConfirmingIssue] = useState(false);
    const [to, setTo] = useState<string[]>([]);
    const [cc, setCc] = useState<string[]>([]);

    /**
     * Re-seeded whenever a different order opens the dialog.
     *
     * TO COMES FROM THE SUPPLIER RECORD, NOT FROM LAST TIME. `sentTo` is one joined string of
     * everyone who was told, To and CC together, so it cannot be split back into the two fields
     * without guessing. Starting from the manufacturer's own addresses is the honest default;
     * anyone added last time is added again deliberately rather than by accident.
     */
    useEffect(() => {
        /*
         * Seeded from the saved book, falling back to the supplier record. A configured `to`
         * is a deliberate choice about who at this manufacturer reads orders, so it wins over
         * the generic address; with none saved, nothing changes from before.
         */
        const defaults = orderEmailDefaultsFor(
            config.data,
            order?.productionLocationId ?? null,
        );
        setTo(defaults.to.length > 0 ? defaults.to : (order?.defaultRecipients ?? []));
        setCc(defaults.cc);
        setConfirmingIssue(false);
    }, [order?.id, order?.defaultRecipients, order?.productionLocationId, config.data]);

    if (!order) return null;

    const isResend = Boolean(order.sentAt);
    const isDraft = order.status === 'DRAFT';

    const toKeys = new Set(to.map((email) => email.trim().toLowerCase()));
    const droppedDefaults =
        order.defaultRecipients.length > 0
        && !order.defaultRecipients.some((email) => toKeys.has(email.trim().toLowerCase()));

    const submit = async () => {
        setIsSending(true);
        try {
            const result = await sendProductionOrder(order.id, { to, cc });
            showToastSuccess({
                title: `${order.code} sent`,
                subtitle: result.sentTo
                    ? `Emailed to ${result.sentTo} with a PDF attached.`
                    : 'Emailed with a PDF attached.',
            });
            onSent();
            onClose();
        } catch (err: any) {
            showToastError({
                title: `Could not send ${order.code}`,
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setIsSending(false);
        }
    };

    /**
     * The same act minus the telling: the PDF is rendered and stored and the statuses move,
     * but nobody is emailed - for the brand that downloads the document and sends it through
     * its own channel. As final as a send (the manufacturer sees it on their portal, materials
     * are earmarked), which is why it sits behind a confirm step - one rendered INLINE in the
     * footer, because a second modal stacked on this one loses the z-index fight and renders
     * greyed out behind the overlay.
     */
    const issueWithoutEmail = async () => {
        setIsIssuing(true);
        try {
            await sendProductionOrder(order.id, { withoutEmail: true });
            showToastSuccess({
                title: `${order.code} issued - no email sent`,
                subtitle:
                    'The PDF is ready in the Document column. Share it through your own channel.',
            });
            onSent();
            onClose();
        } catch (err: any) {
            showToastError({
                title: `Could not issue ${order.code}`,
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setIsIssuing(false);
        }
    };

    return (
        <Modal
            opened={Boolean(order)}
            onClose={onClose}
            title={
                confirmingIssue
                    ? `Issue ${order.code} without emailing?`
                    : isResend
                      ? `Resend ${order.code}`
                      : `Send ${order.code}`
            }
            centered
            size="md"
        >
            {confirmingIssue ? (
                /*
                 * THE CONFIRM REPLACES THE FORM. Left underneath the To/CC fields it read as
                 * one more paragraph of a dialog that was still about emailing; the person
                 * has left that question, and Go back is the way back to it.
                 */
                <Stack gap={16}>
                    <Text fz={14} c={neutralColors.textPrimary}>
                        The order moves to Issued and its PDF is stored — download it from
                        the Document column to share it yourself.{' '}
                        {order.productionLocationName} will see it on their portal, but no
                        email goes out from Reparo.
                    </Text>
                    <Group justify="flex-end" gap={12}>
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            onClick={() => setConfirmingIssue(false)}
                            disabled={isIssuing}
                        >
                            Go back
                        </Button>
                        <Button
                            variant={ButtonVariant.ACTION}
                            loading={isIssuing}
                            onClick={issueWithoutEmail}
                        >
                            Issue order
                        </Button>
                    </Group>
                </Stack>
            ) : (
            <Stack gap={16}>
                <Text fz={14} c={neutralColors.textPrimary}>
                    {order.productionLocationName} will receive a PDF of this order —{' '}
                    {order.lineCount} {order.lineCount === 1 ? 'SKU' : 'SKUs'},{' '}
                    {formatCount(order.totalQuantity)} units.
                </Text>

                {/*
                 * EDITABLE, PRE-FILLED FROM THE SUPPLIER RECORD.
                 *
                 * It showed the address read-only before, which meant the one thing somebody
                 * usually wants on a resend — adding a colleague — could not be done at all.
                 * Pre-filling still puts the real address in front of them, which is what the
                 * confirmation is for.
                 */}
                <TagsInput
                    label="To"
                    description="Type an address and press Enter."
                    placeholder="name@company.com"
                    value={to}
                    onChange={setTo}
                />

                <TagsInput
                    label="CC"
                    description="Anyone else who should see this order."
                    placeholder="name@company.com"
                    value={cc}
                    onChange={setCc}
                />

                {/*
                 * A nudge, not a block. If the supplier record is wrong the durable fix is on
                 * that record — otherwise every future order goes the same wrong way — but
                 * somebody sending to a colleague this once should not be argued with.
                 */}
                {droppedDefaults && (
                    <Text fz={12} c={semanticColors.warning.dark}>
                        This won&apos;t reach the address on {order.productionLocationName}&apos;s
                        supplier record. Update it there if it is wrong.
                    </Text>
                )}

                <Text fz={12} c={neutralColors.textMuted}>
                    It will also appear on their portal, where they can confirm receipt and
                    record production against it.
                </Text>

                {isResend && (
                    <Text fz={12} c={neutralColors.textSecondary}>
                        This order was last sent on{' '}
                        {new Date(order.sentAt as string).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                        })}
                        . Sending again replaces the stored PDF with a fresh copy.
                    </Text>
                )}

                <Group justify={isDraft ? 'space-between' : 'flex-end'} gap={12}>
                    {/* Only a draft has an issue left to perform - once issued, this
                        dialog is purely about (re)sending the email. */}
                    {isDraft && (
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            disabled={isSending}
                            onClick={() => setConfirmingIssue(true)}
                        >
                            Issue without emailing
                        </Button>
                    )}
                    <Group gap={12}>
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            onClick={onClose}
                            disabled={isSending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={ButtonVariant.ACTION}
                            loading={isSending}
                            // The server refuses an empty list; saying so here costs a
                            // round trip less than finding out.
                            disabled={to.length === 0}
                            onClick={submit}
                        >
                            {isResend ? 'Resend' : 'Send'}
                        </Button>
                    </Group>
                </Group>
            </Stack>
            )}
        </Modal>
    );
};

export default SendProductionOrderModal;
